import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useMemo, useState, useEffect } from 'react';
import Joyride, { CallBackProps, Step, STATUS } from 'react-joyride';

import { getPropertyManagerMenuItems } from '@/@mantis/menu-items/property-manager-menu-items';
import { NavItemType } from '@/@mantis/types/menu';
import { Map01Icon } from '@/assets/icons/Map01Icon';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { useCurrentUserAdmin } from '@/hooks/useCurrentUserAdmin';
import { setTourGuideViewedInLocalStorage } from '@/localStorage/tourGuide';

/**
 * A component that provides a guided tour of the menu items using react-joyride
 */
const MenuTourGuide: React.FC = () => {
  const theme = useTheme();
  const { isMenuTourGuideOpen, setIsMenuTourGuideOpen } = useOnboardingProgress();
  const { isSingleFamilyAccount, isPropertyOwner, selectedPropertyLocation } = usePropertyManager();
  const currentUserAdminQuery = useCurrentUserAdmin();

  const [availableSteps, setAvailableSteps] = useState<Step[]>([]);

  // Helper function to flatten nested menu items
  const flattenMenuItems = useCallback(
    (items: NavItemType[]): NavItemType[] =>
      items.reduce<NavItemType[]>((acc, item) => {
        acc.push(item);

        if (item.children) {
          acc.push(...flattenMenuItems(item.children));
        }

        return acc;
      }, []),
    []
  );

  const steps: Step[] = useMemo(() => {
    const menuItems = getPropertyManagerMenuItems(
      isSingleFamilyAccount,
      isPropertyOwner,
      currentUserAdminQuery.data || false,
      undefined,
      undefined,
      selectedPropertyLocation?.is_rental_property
    );
    const flatMenuItems = flattenMenuItems(menuItems);

    // Filter for menu items that will actually be visible and have valid targets
    const visibleMenuItems = flatMenuItems.filter(item => {
      // Exclude collapse items and dividers
      if (item.type === 'collapse' || item.type === 'divider') {
        return false;
      }

      // Exclude group items that don't have a URL (they're just containers)
      if (item.type === 'group' && !item.url) {
        return false;
      }

      // Only include items that have an id (needed for targeting)
      if (!item.id) {
        return false;
      }

      // For property owners, exclude certain items that might not be visible
      if (isPropertyOwner) {
        // Add any specific filtering logic for property owners here if needed
        return true;
      }

      return true;
    });

    // Create steps for each visible menu item
    const tourSteps: Step[] = visibleMenuItems.map((item, index) => ({
      target: `#${item.id}`,
      content: (
        <Box>
          <Typography variant="subtitle1">{item.title}</Typography>
          <Typography variant="body2">
            {item.description || 'Navigate here to access this feature.'}
          </Typography>
        </Box>
      ),
      disableBeacon: true,
      placement: 'right',
      // Add step number for better tracking
      title: `Step ${index + 1} of ${visibleMenuItems.length + (isSingleFamilyAccount ? 0 : 1)}`,
    }));

    // Include property location dropdown in the tour only for multi-family accounts
    if (!isSingleFamilyAccount) {
      tourSteps.push({
        target: '#property-location-selector',
        content: (
          <Box>
            <Typography variant="subtitle1">Your Property</Typography>
            <Typography variant="body2">
              Use this dropdown to switch between different properties you manage. Changing the
              property will update the data shown throughout the application.
            </Typography>
          </Box>
        ),
        disableBeacon: true,
        placement: 'top',
        title: `Step ${tourSteps.length + 1} of ${tourSteps.length + 1}`,
      });
    }

    return tourSteps;
  }, [
    isSingleFamilyAccount,
    isPropertyOwner,
    currentUserAdminQuery.data,
    flattenMenuItems,
    selectedPropertyLocation?.is_rental_property,
  ]);

  // Filter steps based on elements that actually exist in the DOM
  useEffect(() => {
    if (!isMenuTourGuideOpen) return;

    const checkElementsAndUpdateSteps = () => {
      const filteredSteps = steps.filter(step => {
        const target = typeof step.target === 'string' ? step.target : '';
        if (!target) return false;

        // Check if the target element exists in the DOM
        const element = document.querySelector(target);
        return element !== null;
      });

      // Recalculate step titles with correct numbering
      const stepsWithUpdatedTitles = filteredSteps.map((step, index) => ({
        ...step,
        title: `Step ${index + 1} of ${filteredSteps.length}`,
      }));

      setAvailableSteps(stepsWithUpdatedTitles);
    };

    // Check immediately
    checkElementsAndUpdateSteps();

    // Also check after a short delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkElementsAndUpdateSteps, 100);

    return () => clearTimeout(timeoutId);
  }, [steps, isMenuTourGuideOpen]);

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action } = data;

      // End the tour when it's finished, skipped or the close button is clicked
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === 'close') {
        setIsMenuTourGuideOpen(false);
        setTourGuideViewedInLocalStorage(true);
      }
    },
    [setIsMenuTourGuideOpen]
  );

  return (
    <>
      <Tooltip title="Get a guided tour of the platform features">
        <IconButton size="small" color="primary" onClick={() => setIsMenuTourGuideOpen(true)}>
          <Map01Icon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Joyride
        steps={availableSteps}
        run={isMenuTourGuideOpen}
        continuous
        scrollToFirstStep
        showProgress
        showSkipButton
        disableOverlayClose
        spotlightClicks
        callback={handleJoyrideCallback}
        locale={{
          back: 'Previous',
          close: 'Close',
          last: 'Finish',
          next: 'Next',
          nextLabelWithProgress: 'Next ({step} of {steps})',
          open: 'Open',
          skip: 'Skip',
        }}
        styles={{
          options: {
            primaryColor: theme.palette.primary.main,
            backgroundColor: theme.palette.background.paper,
            textColor: theme.palette.text.primary,
            zIndex: 2000,
          },
          buttonNext: {
            backgroundColor: theme.palette.primary.main,
          },
          buttonBack: {
            color: theme.palette.primary.main,
          },
        }}
      />
    </>
  );
};

export default MenuTourGuide;
